import { useEffect } from 'react';
import { useInboxFeed } from '@/hooks/useInboxFeed';
import type { InboxEvent, InboxEventKind } from '@/lib/inbox/events';
import { createMissionSpineRunId, recordMissionSpineMarker } from '@/lib/mission-spine/instrumentation';

export function Inbox() {
  const { events, unreadCount, markAllRead, isLoading } = useInboxFeed();
  const trackingRequestCount = events.filter(event => event.kind === 'tracking_request').length;

  useEffect(() => {
    if (trackingRequestCount === 0) return;
    recordMissionSpineMarker({
      runId: createMissionSpineRunId('tracking-request-render'),
      hop: 'tracking_request_render',
      status: 'success',
      namespace: 'ownyou.inbox_events',
      reasonCode: 'tracking_rows_rendered',
      counts: { trackingRequestCount },
    });
  }, [trackingRequestCount]);

  return (
    <section data-testid="inbox-screen" className="mx-auto w-full max-w-3xl px-4 py-6">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <p
            className="text-xs uppercase"
            style={{ color: 'var(--aurora-ink-dim)', fontFamily: 'var(--aurora-font-mono)' }}
          >
            Inbox
          </p>
          <h1
            className="text-3xl font-semibold"
            style={{ color: 'var(--aurora-ink)', fontFamily: 'var(--aurora-font)' }}
          >
            Updates
          </h1>
        </div>
        <button
          type="button"
          data-testid="inbox-chat-trigger"
          onClick={() => window.dispatchEvent(new CustomEvent('ownyou:open-chat'))}
          className="rounded-full px-4 py-2 text-sm font-medium"
          style={{ background: 'var(--aurora-ink)', color: 'var(--aurora-card-solid)' }}
        >
          Ask
        </button>
      </div>

      <div
        className="rounded-2xl border p-5"
        style={{
          background: 'var(--aurora-card)',
          borderColor: 'var(--aurora-ink-line)',
          boxShadow: 'var(--aurora-card-shadow)',
        }}
      >
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold" style={{ color: 'var(--aurora-ink)' }}>
              Mission updates
            </h2>
            <p className="mt-1 text-sm" style={{ color: 'var(--aurora-ink-dim)' }}>
              {unreadCount > 0
                ? `${unreadCount} unread update${unreadCount === 1 ? '' : 's'}`
                : 'No unread mission updates yet.'}
            </p>
          </div>
          {unreadCount > 0 && (
            <button
              type="button"
              data-testid="inbox-mark-read"
              onClick={markAllRead}
              className="rounded-full px-3 py-1.5 text-sm font-medium"
              style={{ background: 'var(--aurora-accent)', color: 'var(--aurora-card-solid)' }}
            >
              Mark read
            </button>
          )}
        </div>

        <div className="mt-5 divide-y" style={{ borderColor: 'var(--aurora-ink-line)' }}>
          {isLoading ? (
            <p data-testid="inbox-loading" className="py-4 text-sm" style={{ color: 'var(--aurora-ink-dim)' }}>
              Loading updates...
            </p>
          ) : events.length === 0 ? (
            <p data-testid="inbox-empty-state" className="py-4 text-sm" style={{ color: 'var(--aurora-ink-dim)' }}>
              No mission updates yet.
            </p>
          ) : (
            events.map(event => (
              <InboxEventRow key={event.id} event={event} />
            ))
          )}
        </div>
      </div>
    </section>
  );
}

function InboxEventRow({ event }: { event: InboxEvent }) {
  return (
    <article
      data-testid="inbox-event-row"
      data-kind={event.kind}
      data-read={event.readAt ? 'true' : 'false'}
      className="flex items-start gap-3 py-4"
    >
      <span
        className="mt-1 inline-flex h-2.5 w-2.5 shrink-0 rounded-full"
        style={{ background: event.readAt ? 'var(--aurora-ink-line)' : 'var(--aurora-accent)' }}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--aurora-ink)' }}>
            {labelForKind(event.kind)}
          </h3>
          <time className="text-xs" style={{ color: 'var(--aurora-ink-dim)' }}>
            {new Date(event.createdAt).toLocaleString()}
          </time>
        </div>
        {event.title && (
          <p className="mt-1 text-sm" style={{ color: 'var(--aurora-ink)' }}>
            {event.title}
          </p>
        )}
        {event.summary && (
          <p className="mt-1 text-sm" style={{ color: 'var(--aurora-ink-dim)' }}>
            {event.summary}
          </p>
        )}
      </div>
    </article>
  );
}

function labelForKind(kind: InboxEventKind): string {
  switch (kind) {
    case 'mission_lifecycle':
      return 'Lifecycle';
    case 'mission_reaction':
      return 'Reaction';
    case 'change_alert':
      return 'Change';
    case 'tracking_request':
      return 'Tracking request';
  }
}
